import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CONTACT } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in every field.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. We'll get back to you shortly.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.section}
      className="relative py-24 md:py-36 px-6 md:px-10 max-w-7xl mx-auto"
    >
      <div className="grid md:grid-cols-12 gap-14 md:gap-6">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeUp}
          className="md:col-span-5"
        >
          <span className="text-xs tracking-[0.25em] uppercase font-medium text-brand">
            Contact
          </span>
          <h2 className="font-display text-3xl md:text-5xl leading-tight tracking-tight text-ink mt-4">
            Have a problem worth solving?
          </h2>
          <p className="mt-6 text-base leading-relaxed text-muted-foreground max-w-sm">
            Tell us where it hurts. We'll tell you honestly whether strategy,
            tech, product — or all three — is the fix.
          </p>

          <div className="mt-10 flex flex-col gap-5">
            <a
              data-testid={CONTACT.emailLink}
              href="mailto:info@pulseagentic.in"
              className="flex items-center gap-3 text-sm text-ink hover:text-brand transition-colors"
            >
              <Mail size={16} className="text-brand" />
              info@pulseagentic.in
            </a>
            <a
              data-testid={CONTACT.phoneLink}
              href="tel:+919962460345"
              className="flex items-center gap-3 text-sm text-ink hover:text-brand transition-colors"
            >
              <Phone size={16} className="text-brand" />
              +91 99624 60345
            </a>
            <div data-testid={CONTACT.address} className="flex items-start gap-3 text-sm text-muted-foreground max-w-xs">
              <MapPin size={16} className="text-brand mt-0.5 shrink-0" />
              Block No. 14, Plot No. 3B, UR Nagar, Park Road, Padi, Ambattur, Tiruvallur - 600050, Tamil Nadu
            </div>
          </div>
        </motion.div>

        <motion.form
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeUp}
          onSubmit={handleSubmit}
          className="md:col-span-7 flex flex-col gap-5"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <Input
              data-testid={CONTACT.nameInput}
              placeholder="Your name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="h-12 bg-card border-border/70 rounded-sm"
            />
            <Input
              data-testid={CONTACT.emailInput}
              type="email"
              placeholder="Your email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="h-12 bg-card border-border/70 rounded-sm"
            />
          </div>
          <Textarea
            data-testid={CONTACT.messageInput}
            placeholder="What are you trying to solve?"
            rows={6}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="bg-card border-border/70 rounded-sm resize-none"
          />
          <Button
            data-testid={CONTACT.submitButton}
            type="submit"
            disabled={submitting}
            className="self-start bg-ink text-white hover:bg-brand rounded-full px-8 h-12 mt-2"
          >
            {submitting ? "Sending..." : "Send message"}
          </Button>
        </motion.form>
      </div>
    </section>
  );
}
